import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DocumentNeedToggle from './DocumentNeedToggle';
import { newJobDocument } from '../lib/jobDocuments';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import api from '../lib/api';
import { supabase } from '../lib/supabase';
import ImageUpload from './ImageUpload';
import RichTextEditor from './RichTextEditor';
import { BOHOL_MUNICIPALITIES } from '../data/boholLocations';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { COMMON_BENEFITS } from '../lib/constants';
import { Plus, X, FileText } from 'lucide-react';

const EditJobModal = ({ isOpen, onClose, job, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    salary_min: '',
    salary_max: '',
    salary_period: 'monthly',
    locations: [],
    street_address: '',
    category_id: '',
    type: 'permanent',
    work_schedule: 'full_time',
    requirements: '',
    benefits: [],
    image_url: null,
    image_position: 50,
    urgent_hiring: false,
    no_experience_needed: false,
    with_housing: false,
    custom_question: '',
    required_documents: [],
    positions_available: 1
  });

  useEffect(() => {
    if (job) {
      // Convert requirements array to newline-separated string for display
      const requirementsStr = Array.isArray(job.requirements)
        ? job.requirements.join('\n')
        : (job.requirements || '');

      // Convert benefits to array if it's a string, filter out empty values
      let benefitsArray = [];
      if (Array.isArray(job.benefits)) {
        benefitsArray = job.benefits.filter(b => b && typeof b === 'string' && b.trim());
      } else if (typeof job.benefits === 'string' && job.benefits) {
        benefitsArray = job.benefits.split(',').map(b => b.trim()).filter(b => b);
      }

      setFormData({
        title: job.title || '',
        description: job.description || '',
        salary_min: job.salary_min || '',
        salary_max: job.salary_max || '',
        salary_period: job.salary_period || 'monthly',
        locations: job.locations || (job.location ? [job.location] : []),
        street_address: job.street_address || '',
        category_id: job.category_id || '',
        type: job.type || 'permanent',
        work_schedule: job.work_schedule || 'full_time',
        requirements: requirementsStr,
        benefits: benefitsArray,
        image_url: job.image_url || null,
        image_position: job.image_position ?? 50,
        urgent_hiring: job.urgent_hiring || false,
        no_experience_needed: job.no_experience_needed || false,
        with_housing: job.with_housing || false,
        custom_question: job.custom_question || '',
        required_documents: job.required_documents || [],
        positions_available: job.positions_available || 1
      });

    }
  }, [job]);

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error(t('post.categoryFetchFailed', 'Failed to load categories. Please refresh the page.'));
    }
  };

  const toggleBenefit = (benefit) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.includes(benefit)
        ? prev.benefits.filter(b => b !== benefit)
        : [...prev.benefits, benefit]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error(t('job.pleaseLoginEdit', 'Please log in to edit jobs'));
        setLoading(false);
        return;
      }

      // ===== INPUT VALIDATION =====
      // Validate title
      if (!formData.title || formData.title.trim().length < 3) {
        toast.error(t('job.titleTooShort', 'Job title must be at least 3 characters'));
        setLoading(false);
        return;
      }

      // Validate description
      if (!formData.description || formData.description.trim().length < 20) {
        toast.error(t('job.descriptionTooShort', 'Job description must be at least 20 characters'));
        setLoading(false);
        return;
      }

      // Validate category
      if (!formData.category_id) {
        toast.error(t('job.selectCategory', 'Please select a category'));
        setLoading(false);
        return;
      }

      // Validate locations
      if (!formData.locations || formData.locations.length === 0) {
        toast.error(t('job.selectLocation', 'Please select at least one location'));
        setLoading(false);
        return;
      }

      // Validate salary values if provided - use Math.floor for integers
      const salaryMin = formData.salary_min ? Math.floor(Number(formData.salary_min)) : null;
      const salaryMax = formData.salary_max ? Math.floor(Number(formData.salary_max)) : null;
      if (salaryMin !== null && (isNaN(salaryMin) || salaryMin <= 0)) {
        toast.error(t('job.invalidSalaryMin', 'Please enter a valid minimum salary (must be greater than 0)'));
        setLoading(false);
        return;
      }
      if (salaryMax !== null && (isNaN(salaryMax) || salaryMax <= 0)) {
        toast.error(t('job.invalidSalaryMax', 'Please enter a valid maximum salary (must be greater than 0)'));
        setLoading(false);
        return;
      }
      if (salaryMin !== null && salaryMax !== null && salaryMin > salaryMax) {
        toast.error(t('job.salaryRangeError', 'Minimum salary cannot be greater than maximum salary'));
        setLoading(false);
        return;
      }
      // ===== END INPUT VALIDATION =====

      // Parse requirements from string to array
      const requirements = typeof formData.requirements === 'string'
        ? formData.requirements.split('\n').filter(r => r.trim()).map(r => r.trim())
        : formData.requirements;

      const response = await api.put(`/api/jobs/${job.id}`, {
        ...formData,
        salary_min: salaryMin,
        salary_max: salaryMax,
        requirements
      }, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (response.data.success) {
        toast.success(t('job.updateSuccess', 'Job updated successfully!'));
        onSuccess(response.data.job);
        onClose();
      } else {
        toast.error(response.data.detail || t('job.updateFailed', 'Failed to update job'));
      }
    } catch (error) {
      console.error('Error updating job:', error);
      toast.error(error.response?.data?.detail || t('job.updateFailed', 'Failed to update job'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">{t('job.editTitle', 'Edit Job Post')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* TITLE */}
          <div className="space-y-2">
            <Label htmlFor="title">Job Title *</Label>
            <Input
              id="title"
              placeholder="e.g., Restaurant Server, Construction Worker"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="rounded-xl"
            />
          </div>

          {/* CATEGORY & EMPLOYMENT TYPE */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select
                value={formData.category_id}
                onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                required
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Employment Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value })}
                required
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="permanent">Permanent Employment</SelectItem>
                  <SelectItem value="seasonal">Seasonal</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* WORK SCHEDULE */}
          <div className="space-y-2">
            <Label htmlFor="work_schedule">Work Schedule *</Label>
            <Select
              value={formData.work_schedule}
              onValueChange={(value) => setFormData({ ...formData, work_schedule: value })}
              required
            >
              <SelectTrigger className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full_time">Full Time</SelectItem>
                <SelectItem value="part_time">Part Time</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* LOCATION */}
          <div className="space-y-4 p-4 border rounded-xl bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
              <h3 className="font-semibold text-sm">Job Location</h3>
            </div>

            <div className="space-y-2">
              <Label>Municipalities/Cities *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="flex w-full items-center justify-between rounded-xl border border-input bg-white px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 min-h-[40px]" data-testid="edit-form-locations">
                    <span className={formData.locations.length === 0 ? 'text-muted-foreground' : ''}>
                      {formData.locations.length === 0 ? 'Select municipalities' : `${formData.locations.length} selected`}
                    </span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-50"><path d="m6 9 6 6 6-6"/></svg>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <div className="max-h-[250px] overflow-y-auto p-2" onWheel={(e) => e.stopPropagation()}>
                    {BOHOL_MUNICIPALITIES.map((municipality) => (
                      <label key={municipality} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm">
                        <Checkbox
                          checked={formData.locations.includes(municipality)}
                          onCheckedChange={(checked) => {
                            const next = checked
                              ? [...formData.locations, municipality]
                              : formData.locations.filter(l => l !== municipality);
                            setFormData({ ...formData, locations: next });
                          }}
                        />
                        {municipality}
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              {formData.locations.length > 0 && (
                <p className="text-xs text-muted-foreground">{formData.locations.join(', ')}</p>
              )}
            </div>

            {/* Street Address */}
            <div className="space-y-2">
              <Label htmlFor="street_address">Street Address or Landmark (Optional)</Label>
              <Input
                id="street_address"
                placeholder="e.g. Near Bohol Quality Mall, J.A. Clarin St."
                value={formData.street_address}
                onChange={(e) => setFormData({ ...formData, street_address: e.target.value })}
                className="rounded-xl bg-white"
              />
              <p className="text-xs text-muted-foreground">
                Add specific street, building name, or nearby landmark
              </p>
            </div>
          </div>

          {/* DESCRIPTION */}
          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <RichTextEditor
              value={formData.description}
              onChange={(value) => setFormData({ ...formData, description: value })}
              placeholder="Describe the role, responsibilities, and work environment..."
              className="rounded-xl"
            />
          </div>

          {/* SALARY RANGE */}
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="salary_min">Salary Min (₱) *</Label>
                <Input
                  id="salary_min"
                  type="number"
                  placeholder="15000"
                  value={formData.salary_min}
                  onChange={(e) => setFormData({ ...formData, salary_min: e.target.value })}
                  onWheel={(e) => e.target.blur()}
                  className="rounded-xl"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="salary_max">Salary Max (₱) *</Label>
                <Input
                  id="salary_max"
                  type="number"
                  placeholder="25000"
                  value={formData.salary_max}
                  onChange={(e) => setFormData({ ...formData, salary_max: e.target.value })}
                  onWheel={(e) => e.target.blur()}
                  className="rounded-xl"
                  required
                />
              </div>
            </div>

            {/* SALARY PERIOD & POSITIONS */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="salary_period">Salary Period *</Label>
                <Select
                  value={formData.salary_period}
                  onValueChange={(value) => setFormData({ ...formData, salary_period: value })}
                  required
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">
                      <div>
                        <div className="font-semibold">Monthly</div>
                        <div className="text-xs text-muted-foreground">Salary paid per month</div>
                      </div>
                    </SelectItem>
                    <SelectItem value="daily">
                      <div>
                        <div className="font-semibold">Daily</div>
                        <div className="text-xs text-muted-foreground">Salary paid per day</div>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  This helps match with job seekers' salary expectations
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="positions_available">Number of Positions</Label>
                <Input
                  id="positions_available"
                  type="number"
                  min="1"
                  max="100"
                  placeholder="1"
                  value={formData.positions_available}
                  onChange={(e) => setFormData({ ...formData, positions_available: Math.max(1, parseInt(e.target.value) || 1) })}
                  onWheel={(e) => e.target.blur()}
                  className="rounded-xl"
                />
                <p className="text-xs text-muted-foreground">
                  How many people are you hiring for this position?
                </p>
              </div>
            </div>
          </div>

          {/* REQUIREMENTS */}
          <div className="space-y-2">
            <Label htmlFor="requirements">Requirements (one per line)</Label>
            <Textarea
              id="requirements"
              placeholder="High school diploma&#10;Experience with customer service&#10;Good communication skills"
              value={formData.requirements}
              onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
              rows={4}
              className="rounded-xl"
            />
          </div>

          {/* BENEFITS CHECKBOXES */}
          <div className="space-y-3">
            <Label>Benefits & Perks</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {COMMON_BENEFITS.map((benefit) => (
                <div key={benefit} className="flex items-center space-x-2">
                  <Checkbox
                    id={`benefit-${benefit}`}
                    checked={formData.benefits.includes(benefit)}
                    onCheckedChange={() => toggleBenefit(benefit)}
                  />
                  <label
                    htmlFor={`benefit-${benefit}`}
                    className="text-sm cursor-pointer select-none"
                  >
                    {benefit}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* QUICK FILTERS & TAGS */}
          <div className="space-y-3 border-t pt-6">
            <Label className="text-base font-semibold">Quick Filters & Tags</Label>
            <p className="text-sm text-muted-foreground">
              Help job seekers find your posting with these special tags
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Urgent Hiring */}
              <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-secondary/50 transition-colors">
                <Checkbox
                  id="urgent_hiring"
                  checked={formData.urgent_hiring}
                  onCheckedChange={(checked) => setFormData({ ...formData, urgent_hiring: checked })}
                />
                <div className="flex-1">
                  <label
                    htmlFor="urgent_hiring"
                    className="text-sm font-medium cursor-pointer block"
                  >
                    🔴 Urgent Hiring
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Need to fill this position quickly
                  </p>
                </div>
              </div>

              {/* No Experience Needed */}
              <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-secondary/50 transition-colors">
                <Checkbox
                  id="no_experience_needed"
                  checked={formData.no_experience_needed}
                  onCheckedChange={(checked) => setFormData({ ...formData, no_experience_needed: checked })}
                />
                <div className="flex-1">
                  <label
                    htmlFor="no_experience_needed"
                    className="text-sm font-medium cursor-pointer block"
                  >
                    ⭐ No Experience Needed
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Open to fresh graduates and beginners
                  </p>
                </div>
              </div>

              {/* With Housing */}
              <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-secondary/50 transition-colors">
                <Checkbox
                  id="with_housing"
                  checked={formData.with_housing}
                  onCheckedChange={(checked) => setFormData({ ...formData, with_housing: checked })}
                />
                <div className="flex-1">
                  <label
                    htmlFor="with_housing"
                    className="text-sm font-medium cursor-pointer block"
                  >
                    🏠 With Housing
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Accommodation provided
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CUSTOM SCREENING QUESTION */}
          <div className="space-y-3">
            <Label htmlFor="custom_question" className="text-base font-semibold">
              Screening Question (Optional)
            </Label>
            <p className="text-sm text-muted-foreground">
              Add a question that applicants must answer when applying. Their answer will be visible when reviewing applicants.
            </p>
            <Textarea
              id="custom_question"
              placeholder="e.g., Why are you interested in this position? / Do you have your own transportation?"
              value={formData.custom_question}
              onChange={(e) => setFormData({ ...formData, custom_question: e.target.value })}
              className="rounded-xl bg-white min-h-[80px]"
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground text-right">
              {formData.custom_question.length}/500 characters
            </p>
          </div>

          {/* REQUIRED DOCUMENTS */}
          <div className="space-y-3">
            <Label className="text-base font-semibold flex items-center gap-2">
              <FileText size={18} />
              {t('jobDocs.heading', 'Documents from applicants (optional)')}
            </Label>
            <p className="text-sm text-muted-foreground">
              {t('jobDocs.formHint', "List the documents you'll need. For each one choose \"To apply\" (they must upload it before sending the application) or \"If hired\" (they can apply now and bring it later).")}
            </p>

            {/* Document list */}
            {formData.required_documents.length > 0 && (
              <div className="space-y-2">
                {formData.required_documents.map((doc, index) => (
                  <div key={index} className="flex flex-wrap items-center gap-2 bg-gray-50 rounded-lg p-2">
                    <FileText size={16} className="text-gray-500 shrink-0" />
                    <span className="flex-1 min-w-[7.5rem] text-sm break-words">{doc.name}</span>
                    <DocumentNeedToggle
                      doc={doc}
                      onChange={(updated) => {
                        const newDocs = [...formData.required_documents];
                        newDocs[index] = updated;
                        setFormData({ ...formData, required_documents: newDocs });
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 min-w-[44px] min-h-[44px]"
                      onClick={() => {
                        const newDocs = [...formData.required_documents];
                        newDocs.splice(index, 1);
                        setFormData({ ...formData, required_documents: newDocs });
                      }}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Add document form */}
            {formData.required_documents.length < 10 && (
              <div className="flex gap-2">
                <Input
                  id="new_document_name_edit"
                  placeholder="Document name (e.g., Resume, NBI Clearance)"
                  className="rounded-xl bg-white flex-1"
                  maxLength={50}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const docName = e.target.value.trim();
                      if (docName && !formData.required_documents.some(d => d.name.toLowerCase() === docName.toLowerCase())) {
                        setFormData({
                          ...formData,
                          required_documents: [...formData.required_documents, newJobDocument(docName)]
                        });
                        e.target.value = '';
                      }
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-xl"
                  onClick={() => {
                    const input = document.getElementById('new_document_name_edit');
                    const docName = input.value.trim();
                    if (docName && !formData.required_documents.some(d => d.name.toLowerCase() === docName.toLowerCase())) {
                      setFormData({
                        ...formData,
                        required_documents: [...formData.required_documents, newJobDocument(docName)]
                      });
                      input.value = '';
                    }
                  }}
                >
                  <Plus size={16} className="mr-1" />
                  Add
                </Button>
              </div>
            )}

            {/* Quick add suggestions */}
            <div className="flex flex-wrap gap-2">
              {['Resume', 'NBI Clearance', 'Police Clearance', 'Medical Certificate', 'Birth Certificate'].map((suggestion) => (
                !formData.required_documents.some(d => d.name.toLowerCase() === suggestion.toLowerCase()) && (
                  <Button
                    key={suggestion}
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7 px-2 bg-gray-100 hover:bg-gray-200"
                    onClick={() => {
                      if (formData.required_documents.length < 10) {
                        setFormData({
                          ...formData,
                          required_documents: [...formData.required_documents, newJobDocument(suggestion)]
                        });
                      }
                    }}
                  >
                    + {suggestion}
                  </Button>
                )
              ))}
            </div>

            {formData.required_documents.length >= 10 && (
              <p className="text-xs text-amber-600">Maximum 10 documents allowed</p>
            )}
          </div>

          {/* IMAGE UPLOAD */}
          <div className="space-y-3">
            <ImageUpload
              onImageUpload={(imageUrl) => setFormData(prev => ({ ...prev, image_url: imageUrl }))}
              currentImage={formData.image_url}
              label="Job Image (Optional)"
              imagePosition={formData.image_position}
              onPositionChange={(pos) => setFormData(prev => ({ ...prev, image_position: pos }))}
            />
          </div>

          <DialogFooter className="gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading} className="rounded-xl">
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={loading} className="rounded-xl">
              {loading ? t('common.saving', 'Saving...') : t('common.saveChanges', 'Save Changes')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditJobModal;
